import { useState, useMemo, useRef } from "react"
import { Award, Printer, Star, Lock } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

const PATH_LABELS = {
  broad_stem_tech: "STEM & Technology",
  broad_health_medicine: "Health & Medicine",
  broad_business_finance: "Business & Finance",
  broad_creative_arts: "Creative Arts",
  broad_law_social: "Law & Social Sciences",
}

function CertificatePreview({ studentName, pathLabel, date }) {
  return (
    <div className="relative bg-white rounded-2xl border-4 border-indigo-200 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-indigo-600 via-violet-500 to-teal-500" />

      <div className="absolute inset-0 opacity-[0.03] pointer-events-none select-none flex items-center justify-center">
        <Award className="w-64 h-64 text-indigo-900" />
      </div>

      <div className="relative text-center space-y-4 p-8">
        <div className="flex justify-center">
          <div className="bg-indigo-600 text-white text-xs font-black px-4 py-1 rounded-full tracking-widest uppercase">
            EarlyScape
          </div>
        </div>

        <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">Certificate of Achievement</p>

        <div className="space-y-1">
          <p className="text-slate-500 text-sm">This is to certify that</p>
          <h2 className="text-3xl font-black text-slate-900">{studentName}</h2>
        </div>

        <p className="text-slate-600 text-sm max-w-md mx-auto leading-relaxed">
          has successfully completed the career exploration and development programme in
        </p>

        <div className="inline-block bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-black text-lg px-8 py-3 rounded-2xl">
          {pathLabel}
        </div>

        <p className="text-slate-500 text-sm">
          demonstrating commitment to career readiness and professional development.
        </p>

        <div className="flex justify-center gap-2 py-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
          ))}
        </div>

        <div className="flex justify-between items-end pt-4 border-t border-slate-100">
          <div className="text-left">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Date Awarded</p>
            <p className="text-sm font-bold text-slate-700">{date}</p>
          </div>
          <div className="text-center">
            <div className="w-32 border-b-2 border-slate-300 mb-1" />
            <p className="text-xs text-slate-400">EarlyScape Platform</p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-600 via-violet-500 to-teal-500" />
    </div>
  )
}

function LockedCertCard({ label, progress }) {
  const needed = Math.max(0, 80 - progress)
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 opacity-70">
      <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
        <Lock className="w-5 h-5 text-slate-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-700 truncate">{label}</p>
        <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden w-full">
          <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${progress}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-1">{progress}% complete · {needed}% to go</p>
      </div>
    </div>
  )
}

export default function CertificatesPage() {
  const { student, completedPaths, inProgressPaths } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email) ?? students[0]
    if (!student) return { student: null, completedPaths: [], inProgressPaths: [] }

    const allPaths = student.selectedBroadPaths ?? []
    const goals = (StorageService.read("goals") ?? []).filter(g =>
      (g.userId === student.userId || g.userId === student._id) && g.status === "completed"
    )
    const achievements = (student.achievements ?? []).filter(a => a.unlocked)
    const completed = []
    const inProgress = []

    allPaths.forEach((pathId, idx) => {
      const label = PATH_LABELS[pathId] ?? pathId
      // Prototype: first path earns a certificate if student has any achievements/goals
      const qualifies = idx === 0 && (achievements.length >= 1 || goals.length >= 1)
      if (qualifies) {
        completed.push({
          pathId,
          label,
          awardedDate: new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" }),
        })
      } else {
        inProgress.push({ pathId, label, progress: 35 + idx * 10 })
      }
    })

    return { student, completedPaths: completed, inProgressPaths: inProgress }
  }, [])

  const [selectedId, setSelectedId] = useState(null)
  const activePath = completedPaths.find(p => p.pathId === selectedId) ?? completedPaths[0] ?? null
  const studentName = student
    ? `${student.firstName ?? student.name ?? "Student"} ${student.lastName ?? ""}`.trim()
    : "Student"

  const handlePrint = () => window.print()

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center justify-between print:hidden">
              <div>
                <h1 className="text-3xl font-black text-slate-900">Certificates</h1>
                <p className="text-slate-500 text-sm mt-1">Your earned credentials, ready to share or print.</p>
              </div>
              {activePath && (
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-2.5 rounded-xl transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print Certificate
                </button>
              )}
            </div>

            {completedPaths.length === 0 && !student && (
              <div className="text-center py-16 space-y-3">
                <Award className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="text-slate-500">Complete your profile to earn certificates.</p>
              </div>
            )}

            {completedPaths.length === 0 && student && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center space-y-2">
                <Award className="w-10 h-10 text-amber-400 mx-auto" />
                <p className="font-bold text-slate-800">No certificates earned yet</p>
                <p className="text-sm text-slate-500">Keep making progress on your career paths to unlock your first certificate.</p>
              </div>
            )}

            {completedPaths.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Sidebar list */}
                <div className="lg:col-span-4 space-y-3 print:hidden">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Earned</p>
                  {completedPaths.map(p => (
                    <button
                      key={p.pathId}
                      onClick={() => setSelectedId(p.pathId)}
                      className={`w-full text-left rounded-xl border p-4 flex items-center gap-3 transition-colors ${
                        (activePath?.pathId === p.pathId) ? "bg-indigo-50 border-indigo-300" : "bg-white border-slate-200 hover:border-indigo-200"
                      }`}
                    >
                      <Award className="w-5 h-5 text-amber-500 shrink-0" />
                      <div>
                        <p className="text-sm font-bold text-slate-800">{p.label}</p>
                        <p className="text-xs text-slate-400">{p.awardedDate}</p>
                      </div>
                    </button>
                  ))}

                  {inProgressPaths.length > 0 && (
                    <>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mt-4">In Progress</p>
                      {inProgressPaths.map(p => (
                        <LockedCertCard key={p.pathId} label={p.label} progress={p.progress} />
                      ))}
                    </>
                  )}
                </div>

                {/* Certificate preview */}
                <div className="lg:col-span-8">
                  {activePath && (
                    <CertificatePreview
                      studentName={studentName}
                      pathLabel={activePath.label}
                      date={activePath.awardedDate}
                    />
                  )}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>

      <style>{`
        @media print {
          .lg\\:ml-64, nav, aside, .print\\:hidden { display: none !important; }
          main { margin: 0 !important; padding: 0 !important; }
          body { background: white !important; }
        }
      `}</style>
    </ProtectedRoute>
  )
}
